import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { ArrowsClockwise, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";

// Overlay global: aparece quando o backend não responde (janela de deploy /
// atualização ou oscilação de rede). Faz polling em /api/health e, ao voltar,
// dispara "backend-online" — o interceptor do api.js então reenvia a
// requisição original de forma transparente.
export default function SystemStatusOverlay() {
  const [offline, setOffline] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const onOffline = () => setOffline(true);
    window.addEventListener("backend-offline", onOffline);
    return () => window.removeEventListener("backend-offline", onOffline);
  }, []);

  useEffect(() => {
    if (!offline) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/health`, { cache: "no-store" });
        if (r.ok && !cancelled) {
          setOffline(false);
          setAttempts(0);
          window.dispatchEvent(new CustomEvent("backend-online"));
          toast.success("Sistema atualizado! Continuando de onde você parou…");
          return;
        }
      } catch (err) {
        // ainda fora — tenta de novo (silencioso por design: o backend está em deploy)
        console.debug("health poll: backend ainda offline", err?.message);
      }
      if (!cancelled) {
        setAttempts((a) => a + 1);
        timer.current = setTimeout(poll, 2500);
      }
    };
    timer.current = setTimeout(poll, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [offline]);

  if (!offline) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="system-updating-overlay"
    >
      <div className="bg-white border-2 border-black max-w-md w-full p-8 text-center shadow-[8px_8px_0_0_#FFC000]">
        <div
          className="inline-flex items-center gap-2 bg-black text-[#FFC000] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] mb-5"
          data-testid="system-updating-badge"
        >
          <Wrench size={13} weight="fill" /> Sistema atualizando
        </div>
        <div className="flex justify-center mb-4">
          <ArrowsClockwise size={40} weight="bold" className="animate-spin text-black" />
        </div>
        <h2 className="font-head text-xl font-black mb-2">Só um instante…</h2>
        <p className="text-sm text-zinc-600 leading-relaxed">
          Estamos aplicando uma atualização no sistema (ou a conexão oscilou).
          Isso leva poucos segundos — vamos <strong>reconectar automaticamente</strong> e
          continuar exatamente de onde você parou.
        </p>
        <div className="mt-5 text-[11px] font-mono text-zinc-400" data-testid="system-updating-attempts">
          reconectando{attempts > 0 ? ` · tentativa ${attempts + 1}` : "…"}
        </div>
      </div>
    </div>
  );
}
